/**
 * AudioBusManager — library-level audio mutex.
 *
 * The library has three possible TTS sources competing for the single
 * browser audio output:
 *
 *   1. The main AudioGuide (Guide LLM responses)
 *   2. AdventureManager (task-completion narrations)
 *   3. StoryPointsManager (dense 10m-spaced scenes)
 *
 * Without coordination they would speak on top of each other. The
 * AudioBusManager enforces "only one source is active at a time",
 * with the priority order specified by Alex + Tschepp:
 *
 *     Adventure > StoryPoints > AudioGuide
 *
 * When a higher-priority source wants to speak, lower-priority sources
 * are told to pause. When the high-priority source finishes, it calls
 * release() and the next queued source (if any) takes over.
 *
 * Sources register themselves on construction and call `request(priority)`
 * before speaking. The manager either grants access (returning true) or
 * denies it if a higher-priority source is already speaking.
 */
const PRIORITY_RANK = {
    adventure: 3,
    'story-points': 2,
    ambient: 1,
};
export class AudioBusManager {
    constructor(bus) {
        this._currentPriority = null;
        this._currentSourceId = null;
        this._sources = new Map();
        this._bus = bus;
    }
    /**
     * Register a source with its default priority. Must be called before
     * the source can request the bus. Idempotent — calling twice with the
     * same id just updates the stored source/priority.
     */
    register(source, priority) {
        this._sources.set(source.id, { source, priority });
    }
    /**
     * Unregister a source (e.g. when a hook unmounts). If the unregistering
     * source currently holds the bus, it's automatically released.
     */
    unregister(sourceId) {
        if (this._currentSourceId === sourceId) {
            this.release(sourceId);
        }
        this._sources.delete(sourceId);
    }
    /**
     * Request exclusive access to the audio bus. Returns true if granted.
     * If denied, the calling source should not attempt TTS playback until
     * it tries again later (or until the current holder calls release()).
     *
     * Grant rules:
     *   - If no one holds the bus: grant.
     *   - If the requester has strictly higher priority than the current
     *     holder: grant, and pause the current holder.
     *   - If same or lower priority: deny.
     */
    request(sourceId) {
        const entry = this._sources.get(sourceId);
        if (!entry) {
            return false;
        }
        if (this._currentSourceId === null) {
            this._grant(sourceId, entry.priority);
            return true;
        }
        if (this._currentSourceId === sourceId) {
            // Same source re-requesting while already holding — allow
            return true;
        }
        const currentRank = PRIORITY_RANK[this._currentPriority];
        const requesterRank = PRIORITY_RANK[entry.priority];
        if (requesterRank > currentRank) {
            // Preempt the current holder
            const currentEntry = this._sources.get(this._currentSourceId);
            currentEntry?.source.onPause();
            this._grant(sourceId, entry.priority);
            return true;
        }
        return false;
    }
    /**
     * Release the bus. Only the current holder can release. Other sources
     * can wait and then re-request.
     */
    release(sourceId) {
        if (this._currentSourceId !== sourceId)
            return;
        this._currentPriority = null;
        this._currentSourceId = null;
        this._bus.emit('audio-bus:released', { sourceId });
    }
    /**
     * Returns the id of the source currently holding the bus, or null
     * if no one is speaking.
     */
    get currentHolder() {
        return this._currentSourceId;
    }
    _grant(sourceId, priority) {
        this._currentSourceId = sourceId;
        this._currentPriority = priority;
        this._bus.emit('audio-bus:granted', { sourceId, priority });
    }
}
//# sourceMappingURL=AudioBusManager.js.map